import { RefObject, useCallback, useEffect } from 'react';
import { useRequestAnimationFrame } from './useRequestAnimationFrame';
import { ListenerOptions } from './useWindowListener';

export const useTargetListener = <
  T extends EventTarget,
  K extends keyof WindowEventMap
>(
  ref: RefObject<T | undefined>,
  key: K,
  fn: (arg: Event) => void,
  { throttle = true, passive, once }: ListenerOptions = {},
) => {
  const throttled = useRequestAnimationFrame(fn);

  const actual = throttle ? throttled : fn;

  const unlessDefaultPrevented = useCallback(
    (event: Event) => {
      if (event.defaultPrevented) {
        return;
      }
      actual(event);
    },
    [actual],
  );

  useEffect(() => {
    const target = ref.current;
    if (target == null) {
      return;
    }
    target.addEventListener(key, unlessDefaultPrevented, { passive, once });

    return () => {
      target.removeEventListener(key, unlessDefaultPrevented);
    };
  }, [ref, unlessDefaultPrevented, key, passive, once]);
};
